import { z } from 'zod';

/**
 * THE REST CONTRACT.
 *
 * This file is the single source of truth for every request and response that
 * crosses between `apps/api` and `apps/web`. It is not a best-effort sketch and
 * it is not owned by one side: the server mirrors it in
 * `apps/api/src/schemas/common.ts` (JSON Schema, for ajv/OpenAPI) and
 * `apps/api/src/view/dto.ts` (TypeScript, for the handlers), and
 * `apps/api/src/routes/contract.test.ts` parses REAL `app.inject()` responses
 * with the schemas below for every endpoint the client calls. A drift between
 * the three fails that suite.
 *
 * Three rules govern everything here.
 *
 * 1. **Money is a decimal STRING.** Never `z.number()` for a price, quantity,
 *    fee, margin, P&L or percentage — a JSON number is a binary float and a
 *    binary float silently loses cents. `z.number()` appears only for counts,
 *    periods and millisecond durations.
 *
 * 2. **`null` means "we do not know", and nothing else means it.** Any value
 *    the server cannot obtain — the price when market data is down, the mark
 *    price when the venue is unreachable, an unrealised P&L that depends on
 *    that mark price — is `null` on the wire, and the UI renders an explicit
 *    «нет данных». A placeholder `"0"` or an epoch-zero timestamp is forbidden:
 *    on a trading dashboard a plausible-looking fabricated number is the worst
 *    possible failure mode, strictly worse than a blank.
 *
 * 3. **Enums are closed only where the domain is closed.** Where the domain
 *    genuinely fixes the vocabulary (`TradeState`, `OrderStatus`, `OrderPurpose`,
 *    `SmoothingType`) the schema is a `z.enum` and an unexpected value fails
 *    loudly at the boundary. Where the vocabulary is genuinely open — an audit
 *    trail replayed from rows written by older builds, a `cooldownState` string
 *    owned by the strategy engine — the schema stays `z.string()` and the UI
 *    keeps a defensive fallback that shows the raw token next to a Russian
 *    label, because dropping an unrecognised audit action is worse than
 *    printing an identifier.
 */

export const RoleSchema = z.enum(['VIEWER', 'OPERATOR', 'ADMIN']);
export type Role = z.infer<typeof RoleSchema>;

export const ModeSchema = z.enum(['PAPER', 'VST', 'LIVE']);
export type Mode = z.infer<typeof ModeSchema>;

export const StrategyStateSchema = z.enum(['RUNNING', 'ENTRIES_PAUSED', 'STOPPED', 'ERROR']);
export type StrategyState = z.infer<typeof StrategyStateSchema>;

export const ConnectionStatusSchema = z.enum(['CONNECTED', 'DEGRADED', 'DISCONNECTED']);
export type ConnectionStatus = z.infer<typeof ConnectionStatusSchema>;

export const SideSchema = z.enum(['LONG', 'SHORT']);
export type Side = z.infer<typeof SideSchema>;

export const DecisionOutcomeSchema = z.enum(['LONG', 'SHORT', 'SKIP']);
export type DecisionOutcome = z.infer<typeof DecisionOutcomeSchema>;

export const WarningLevelSchema = z.enum(['INFO', 'WARNING', 'CRITICAL']);
export type WarningLevel = z.infer<typeof WarningLevelSchema>;

/**
 * Mirrors the domain `SmoothingType` exactly. `RMA` (Wilder's smoothing) is a
 * first-class option in `packages/domain`; omitting it here made every RMA
 * configuration unrepresentable on the wire.
 */
export const SmoothingTypeSchema = z.enum(['SMA', 'EMA', 'RMA']);
export type SmoothingType = z.infer<typeof SmoothingTypeSchema>;

/**
 * Mirrors the domain `TradeState` (`packages/domain/src/state-machine.ts`)
 * exactly — the same fourteen states the trade lifecycle machine can be in.
 */
export const TradeStateSchema = z.enum([
  'IDLE',
  'DECISION_PENDING',
  'SKIPPED',
  'ENTRY_SUBMITTING',
  'ENTRY_PARTIALLY_FILLED',
  'PROTECTION_SUBMITTING',
  'POSITION_PROTECTED',
  'EXIT_PARTIALLY_FILLED',
  'CLOSED_TP',
  'CLOSED_SL',
  'CLOSED_MANUAL',
  'CLOSED_LIQUIDATION',
  'EMERGENCY_CLOSING',
  'ERROR_REQUIRES_ATTENTION',
]);
export type TradeState = z.infer<typeof TradeStateSchema>;

export const ActorSchema = z.object({
  role: RoleSchema,
  email: z.string(),
});
export type Actor = z.infer<typeof ActorSchema>;

export const LoginResponseSchema = z.object({
  expiresAt: z.string(),
  actor: ActorSchema,
});
export type LoginResponse = z.infer<typeof LoginResponseSchema>;

export const SessionResponseSchema = z.object({
  actor: ActorSchema,
  expiresAt: z.string(),
  lastReauthAt: z.string().nullable(),
});
export type SessionResponse = z.infer<typeof SessionResponseSchema>;

export const WarningSchema = z.object({
  id: z.string(),
  level: WarningLevelSchema,
  messageRu: z.string(),
  at: z.string(),
});
export type Warning = z.infer<typeof WarningSchema>;

export const IndicatorModeSchema = z.enum(['NORMAL', 'POST_IMPULSE']);
export type IndicatorMode = z.infer<typeof IndicatorModeSchema>;

export const IndicatorsSchema = z.object({
  mode: IndicatorModeSchema,
  timeframe: z.string(),
  /**
   * `null` before the first decision of this build, or whenever market data
   * has not produced a closed candle. It used to be a required string, which
   * forced the server to emit the Unix epoch — a timestamp that looks like a
   * real one and is off by fifty-six years.
   */
  candleCloseTime: z.string().nullable(),
  rsi: z.string().nullable(),
  smoothedRsi: z.string().nullable(),
  preliminarySignal: DecisionOutcomeSchema.nullable(),
});
export type Indicators = z.infer<typeof IndicatorsSchema>;

export const ImpulseInfoSchema = z.object({
  detectedAt: z.string(),
  valuePct: z.string(),
  dayOpen: z.string(),
  knownDayHigh: z.string(),
});
export type ImpulseInfo = z.infer<typeof ImpulseInfoSchema>;

export const CooldownSchema = z.object({
  active: z.boolean(),
  reasonRu: z.string().nullable(),
  remainingSkips: z.number().int().nullable(),
  nextEligibleDate: z.string().nullable(),
  impulse: ImpulseInfoSchema.nullable(),
});
export type Cooldown = z.infer<typeof CooldownSchema>;

export const DecisionRecordSchema = z.object({
  decisionId: z.string(),
  strategyVersion: z.string(),
  mode: ModeSchema,
  venue: z.string(),
  symbol: z.string(),
  localTradingDate: z.string(),
  scheduledAt: z.string(),
  evaluatedAt: z.string(),
  marketDataAsOf: z.string(),
  rsi: z.string().nullable(),
  smoothedRsi: z.string().nullable(),
  timeframe: z.string(),
  impulseValue: z.string().nullable(),
  /**
   * OPEN VOCABULARY, deliberately. `cooldownState` is a free-form string owned
   * by the strategy engine (`DecisionRecord.cooldownState` is `string` in the
   * domain); there is no enum to close it against. The client translates the
   * values it knows and reports anything else as «состояние не распознано»
   * rather than hiding it.
   */
  cooldownState: z.string().nullable(),
  /**
   * CLOSED: this is the trade lifecycle state, so it is a `TradeState`.
   * `null` means the server read a stored value that is not a `TradeState` and
   * refuses to guess which one was meant — the UI shows that explicitly.
   */
  positionState: TradeStateSchema.nullable(),
  decision: DecisionOutcomeSchema,
  reasonCode: z.string(),
});
export type DecisionRecord = z.infer<typeof DecisionRecordSchema>;

export const PositionSchema = z.object({
  symbol: z.string(),
  side: SideSchema,
  quantity: z.string(),
  leverage: z.number().int(),
  entryPrice: z.string(),
  /**
   * `null` when the venue mark price cannot be read. It used to echo the entry
   * price, which renders as a position sitting exactly at break-even — a
   * fabricated number indistinguishable from a real one.
   */
  markPrice: z.string().nullable(),
  liquidationPrice: z.string().nullable(),
  stopLossPrice: z.string().nullable(),
  takeProfitPrice: z.string().nullable(),
  slRoiPct: z.string(),
  tpRoiPct: z.string(),
  marginAmount: z.string(),
  /** `null` whenever `markPrice` is null: the P&L is derived from it. */
  grossPnl: z.string().nullable(),
  netPnl: z.string().nullable(),
  openedAt: z.string(),
  ageWarning: z.boolean(),
});
export type Position = z.infer<typeof PositionSchema>;

/**
 * Mirrors the domain `OrderStatus` (`packages/domain/src/types/execution.ts`).
 *
 *  - `EXPIRED` and `UNKNOWN` are first-class. `UNKNOWN` is the domain's
 *    local-only state meaning *the venue's answer was lost and the order's real
 *    state is not known*; the UI renders it as «требуется сверка». Mapping it
 *    onto any other value — `CANCELLED` especially — tells the operator
 *    something the system does not know.
 *  - `SUBMITTING` and `CANCELLING` are GONE. The domain has no such states, the
 *    persistence layer stores only the domain vocabulary, and no server code
 *    path could ever emit them. Their only use was as a landing pad for
 *    `UNKNOWN`, which is exactly the lie above.
 *  - The contract spells the domain's `CANCELED` as `CANCELLED`; the server
 *    maps the one to the other in `view/mappers.ts`.
 */
export const OrderStatusSchema = z.enum([
  'PENDING',
  'OPEN',
  'PARTIALLY_FILLED',
  'FILLED',
  'CANCELLED',
  'EXPIRED',
  'REJECTED',
  'UNKNOWN',
]);
export type OrderStatus = z.infer<typeof OrderStatusSchema>;

/**
 * Mirrors the domain `OrderPurpose`, plus `UNKNOWN`.
 *
 * `EMERGENCY_CLOSE` and `DUST_CLEANUP` are real, distinct purposes: one is an
 * operator closing a live position at market, the other is the system sweeping
 * a residual few satoshis. Collapsing both into a single `MANUAL_CLOSE` — which
 * is what the contract forced before, and which is not an `OrderPurpose` the
 * domain has at all — made an automated dust sweep read as a human decision.
 * `MANUAL_CLOSE` is therefore removed: nothing can produce it. `UNKNOWN` is the
 * honest bucket for a stored purpose this build does not recognise.
 */
export const OrderKindSchema = z.enum([
  'ENTRY',
  'STOP_LOSS',
  'TAKE_PROFIT',
  'EMERGENCY_CLOSE',
  'DUST_CLEANUP',
  'UNKNOWN',
]);
export type OrderKind = z.infer<typeof OrderKindSchema>;

export const OrderSchema = z.object({
  orderId: z.string(),
  clientOrderId: z.string(),
  kind: OrderKindSchema,
  side: SideSchema,
  status: OrderStatusSchema,
  quantity: z.string(),
  filledQuantity: z.string(),
  price: z.string().nullable(),
  averageFillPrice: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Order = z.infer<typeof OrderSchema>;

export const TradeSchema = z.object({
  tradeId: z.string(),
  symbol: z.string(),
  side: SideSchema,
  entryPrice: z.string(),
  exitPrice: z.string().nullable(),
  quantity: z.string(),
  marginAmount: z.string(),
  grossPnl: z.string().nullable(),
  netPnl: z.string().nullable(),
  fees: z.string(),
  funding: z.string(),
  entryReasonCode: z.string(),
  exitReasonCode: z.string().nullable(),
  openedAt: z.string(),
  closedAt: z.string().nullable(),
});
export type Trade = z.infer<typeof TradeSchema>;

export const AuditSourceSchema = z.enum(['WEB', 'TELEGRAM', 'SYSTEM']);
export type AuditSource = z.infer<typeof AuditSourceSchema>;

/**
 * The audit actions this build knows about — the domain `AuditEventType`.
 *
 * Exported for the client's label table so the two cannot drift, but
 * DELIBERATELY NOT used to type `AuditEntry.action`. The audit trail is
 * append-only and replayed from rows written by every build that ever ran; a
 * closed enum would make one row from an older build fail validation and blank
 * the whole audit page. An audit trail that refuses to render is worse than one
 * that prints an unfamiliar identifier, so `action` stays `z.string()` and the
 * client falls back to «Другое действие (TOKEN)».
 */
export const KnownAuditActionSchema = z.enum([
  'DECISION_EVALUATED',
  'ENTRY_SUBMITTED',
  'ENTRY_FILLED',
  'PROTECTION_CONFIRMED',
  'PROTECTION_FAILED',
  'POSITION_CLOSED',
  'ORDER_CANCELED',
  'STATE_TRANSITION',
  'RECONCILIATION_RUN',
  'RECONCILIATION_MISMATCH',
  'IMPULSE_REGISTERED',
  'COMMAND_RECEIVED',
  'COMMAND_EXECUTED',
  'COMMAND_REJECTED',
  'CONFIG_CHANGED',
  'AUTH_LOGIN_SUCCESS',
  'AUTH_LOGIN_FAILURE',
  'LIVE_GATE_EVALUATED',
  'INCIDENT_OPENED',
  'INCIDENT_RESOLVED',
  'NOTIFICATION_SENT',
  'NOTIFICATION_FAILED',
]);
export type KnownAuditAction = z.infer<typeof KnownAuditActionSchema>;

export const AuditEntrySchema = z.object({
  id: z.string(),
  at: z.string(),
  actorEmail: z.string().nullable(),
  source: AuditSourceSchema,
  /** OPEN VOCABULARY — see `KnownAuditActionSchema` for why. */
  action: z.string(),
  detailsRu: z.string(),
  idempotencyKey: z.string().nullable(),
});
export type AuditEntry = z.infer<typeof AuditEntrySchema>;

/** Last traded price with the instant it was observed. `null` = no market data. */
export const PriceSnapshotSchema = z.object({
  value: z.string(),
  asOf: z.string(),
});
export type PriceSnapshot = z.infer<typeof PriceSnapshotSchema>;

/** Account balance in USDT. `null` = the venue account could not be read. */
export const BalanceSchema = z.object({
  total: z.string(),
  available: z.string(),
  reserved: z.string(),
});
export type Balance = z.infer<typeof BalanceSchema>;

export const DashboardResponseSchema = z.object({
  mode: ModeSchema,
  strategyState: StrategyStateSchema,
  connections: z.object({
    marketData: ConnectionStatusSchema,
    executionVenue: ConnectionStatusSchema,
    telegram: ConnectionStatusSchema,
  }),
  symbol: z.string(),
  /**
   * `null` when market data is unavailable — which `connections.marketData`
   * reports as `DISCONNECTED` and `recentWarnings` explains in Russian. The
   * whole object goes null together: without a price there is no `asOf`
   * either, and an `asOf` without a value is meaningless.
   */
  price: PriceSnapshotSchema.nullable(),
  /** `null` when the venue balance cannot be read. */
  balance: BalanceSchema.nullable(),
  position: PositionSchema.nullable(),
  nextRun: z.object({ scheduledAt: z.string().nullable(), timezone: z.string() }),
  indicators: IndicatorsSchema,
  cooldown: CooldownSchema,
  lastDecision: DecisionRecordSchema.nullable(),
  recentWarnings: z.array(WarningSchema),
});
export type DashboardResponse = z.infer<typeof DashboardResponseSchema>;

export const StrategyConfigSchema = z.object({
  strategyVersion: z.string(),
  symbol: z.string(),
  timezone: z.string(),
  decisionTime: z.string(),
  entryWindowMinutes: z.number(),
  rsiPeriod: z.number(),
  smoothingType: SmoothingTypeSchema,
  smoothingPeriod: z.number(),
  overboughtThreshold: z.number(),
  neutralThreshold: z.number(),
  impulseThresholdPct: z.string(),
  cooldownSessions: z.number(),
  leverage: z.number(),
  slRoiPct: z.string(),
  tpRoiPct: z.string(),
  marginAmount: z.string(),
});
export type StrategyConfig = z.infer<typeof StrategyConfigSchema>;

export const PreviewResponseSchema = z.object({
  decision: DecisionOutcomeSchema,
  reasonCode: z.string(),
  rsi: z.string().nullable(),
  smoothedRsi: z.string().nullable(),
  entryPriceEstimate: z.string().nullable(),
  stopPrice: z.string().nullable(),
  takePrice: z.string().nullable(),
  liquidationPriceEstimate: z.string().nullable(),
  notional: z.string().nullable(),
  quantity: z.string().nullable(),
  estimatedFees: z.string().nullable(),
});
export type PreviewResponse = z.infer<typeof PreviewResponseSchema>;

export const ConnectionKindSchema = z.enum(['MARKET_DATA', 'EXECUTION_VENUE', 'TELEGRAM']);
export type ConnectionKind = z.infer<typeof ConnectionKindSchema>;

export const ConnectionSchema = z.object({
  id: z.string(),
  name: z.string(),
  kind: ConnectionKindSchema,
  status: ConnectionStatusSchema,
  lastCheckedAt: z.string().nullable(),
  detailsRu: z.string(),
  keyFingerprint: z.string().nullable(),
});
export type Connection = z.infer<typeof ConnectionSchema>;

/**
 * Result of `POST /api/connections/:id/test`.
 *
 * A probe result, not a connection: it reports what one specific check found
 * at one specific instant. The refreshed `Connection` row is read back from
 * `GET /api/connections`, so a probe can never silently rewrite the list.
 */
export const ConnectionTestResultSchema = z.object({
  id: z.string(),
  ok: z.boolean(),
  /** Round-trip latency in milliseconds, or `null` when the probe never answered. */
  latencyMs: z.number().nullable(),
  detailsRu: z.string(),
  checkedAt: z.string(),
});
export type ConnectionTestResult = z.infer<typeof ConnectionTestResultSchema>;

export const BacktestStatusSchema = z.enum(['QUEUED', 'RUNNING', 'COMPLETED', 'FAILED']);
export type BacktestStatus = z.infer<typeof BacktestStatusSchema>;

export const BacktestSummarySchema = z.object({
  id: z.string(),
  status: BacktestStatusSchema,
  createdAt: z.string(),
  from: z.string(),
  to: z.string(),
});
export type BacktestSummary = z.infer<typeof BacktestSummarySchema>;

export const BacktestMetricsSchema = z.object({
  decisions: z.number(),
  trades: z.number(),
  longShare: z.string(),
  shortShare: z.string(),
  skipShare: z.string(),
  winRate: z.string(),
  grossPnl: z.string(),
  netPnl: z.string(),
  fees: z.string(),
  funding: z.string(),
  maxDrawdown: z.string(),
  profitFactor: z.string(),
  averageTradeResult: z.string(),
  worstLossStreak: z.number(),
  cooldownEvents: z.number(),
  liquidations: z.number(),
});
export type BacktestMetrics = z.infer<typeof BacktestMetricsSchema>;

export const BacktestDetailSchema = BacktestSummarySchema.extend({
  metrics: BacktestMetricsSchema.nullable(),
  monthly: z.array(z.object({ month: z.string(), netPnl: z.string(), trades: z.number() })),
  trades: z.array(TradeSchema),
  errorRu: z.string().nullable(),
});
export type BacktestDetail = z.infer<typeof BacktestDetailSchema>;

export const HealthReadySchema = z.object({
  ready: z.boolean(),
  version: z.string(),
  serverTime: z.string(),
  venueTime: z.string().nullable(),
  clockSkewMs: z.number().nullable(),
  queue: z.object({ pending: z.number(), failed: z.number() }),
  incidents: z.array(
    z.object({
      id: z.string(),
      level: WarningLevelSchema,
      messageRu: z.string(),
      at: z.string(),
      resolvedAt: z.string().nullable(),
    }),
  ),
});
export type HealthReady = z.infer<typeof HealthReadySchema>;

export const ApiErrorBodySchema = z.object({
  error: z.object({
    code: z.string(),
    messageRu: z.string(),
    details: z.array(z.object({ field: z.string().optional(), messageRu: z.string() })),
    correlationId: z.string(),
  }),
});
export type ApiErrorBody = z.infer<typeof ApiErrorBodySchema>;

// --- List envelopes --------------------------------------------------------
//
// Every collection endpoint answers with the SAME envelope: `{ items, page }`.
// One shape for all seven lists, rather than seven bespoke keys (`{orders}`,
// `{trades}`, `{entries}`, ...), because:
//
//  - the pagination block is not optional information. `hasMore` is how the
//    audit and decision screens know they are showing the first 50 of 4 000
//    rows. A bare `{entries: [...]}` silently truncates, and a silently
//    truncated audit trail is the same class of defect as a fabricated price;
//  - one envelope means one client helper and one server helper, so a new
//    collection endpoint cannot invent an eighth spelling.

export const PageInfoSchema = z.object({
  limit: z.number().int(),
  offset: z.number().int(),
  /** True when rows exist beyond `offset + items.length`. */
  hasMore: z.boolean(),
});
export type PageInfo = z.infer<typeof PageInfoSchema>;

function listResponse<T extends z.ZodTypeAny>(item: T) {
  return z.object({ items: z.array(item), page: PageInfoSchema });
}

/**
 * `GET /api/positions`. A list, not a single position: the MVP trades one
 * symbol, but the venue can hold more than one open trade during a partial
 * exit, and a shape that can only express one of them would hide the others.
 */
export const PositionsResponseSchema = listResponse(PositionSchema);
export type PositionsResponse = z.infer<typeof PositionsResponseSchema>;

export const OrdersResponseSchema = listResponse(OrderSchema);
export type OrdersResponse = z.infer<typeof OrdersResponseSchema>;

export const TradesResponseSchema = listResponse(TradeSchema);
export type TradesResponse = z.infer<typeof TradesResponseSchema>;

export const DecisionsResponseSchema = listResponse(DecisionRecordSchema);
export type DecisionsResponse = z.infer<typeof DecisionsResponseSchema>;

export const AuditResponseSchema = listResponse(AuditEntrySchema);
export type AuditResponse = z.infer<typeof AuditResponseSchema>;

export const ConnectionsResponseSchema = listResponse(ConnectionSchema);
export type ConnectionsResponse = z.infer<typeof ConnectionsResponseSchema>;

export const BacktestListResponseSchema = listResponse(BacktestSummarySchema);
export type BacktestListResponse = z.infer<typeof BacktestListResponseSchema>;

// --- Requests --------------------------------------------------------------

/**
 * Body of `POST /api/strategy/preview`. The operator previews with the values
 * currently in the form, which may not be saved yet, so every field is
 * optional and the server falls back to the stored configuration for anything
 * omitted. Read-only: this endpoint places nothing and therefore carries no
 * `Idempotency-Key`.
 */
export const PreviewRequestSchema = StrategyConfigSchema.partial();
export type PreviewRequest = z.infer<typeof PreviewRequestSchema>;

/** Body of `POST /api/backtests`. Dates are plain `ГГГГ-ММ-ДД` calendar days. */
export const BacktestRequestSchema = z.object({
  from: z.string(),
  to: z.string(),
  strategyVersion: z.string().nullable().optional(),
});
export type BacktestRequest = z.infer<typeof BacktestRequestSchema>;

/** Body accepted by every command route. `reasonRu` lands in the audit entry. */
export const CommandRequestSchema = z.object({
  reasonRu: z.string().optional(),
  totpCode: z.string().nullable().optional(),
});
export type CommandRequest = z.infer<typeof CommandRequestSchema>;

// --- Commands --------------------------------------------------------------

/**
 * The six operator commands, each served by `POST /api/commands/<kebab-case>`:
 *
 *   START_STRATEGY   -> /api/commands/start-strategy
 *   PAUSE_ENTRIES    -> /api/commands/pause-entries
 *   RESUME_ENTRIES   -> /api/commands/resume-entries
 *   RUN_NOW          -> /api/commands/run-now
 *   EMERGENCY_CLOSE  -> /api/commands/emergency-close
 *   CANCEL_ORDERS    -> /api/commands/cancel-orders
 *
 * One prefix for all six, so all six provably go through the one pipeline that
 * claims the `Idempotency-Key` in PostgreSQL, writes `COMMAND_RECEIVED`, runs
 * the effect exactly once and writes `COMMAND_EXECUTED`/`COMMAND_REJECTED`.
 * `/strategy/*` is the configuration resource (`GET`/`PUT /strategy/config`,
 * `POST /strategy/preview`); mixing commands into it is how a future command
 * ends up bypassing that pipeline.
 */
export const CommandKindSchema = z.enum([
  'START_STRATEGY',
  'PAUSE_ENTRIES',
  'RESUME_ENTRIES',
  'RUN_NOW',
  'EMERGENCY_CLOSE',
  'CANCEL_ORDERS',
]);
export type CommandKind = z.infer<typeof CommandKindSchema>;

/** Lifecycle of a command row. A 2xx body is `EXECUTED`, or a replay of any of these. */
export const CommandStatusSchema = z.enum(['PENDING', 'EXECUTED', 'REJECTED', 'FAILED']);
export type CommandStatus = z.infer<typeof CommandStatusSchema>;

/**
 * The result of a control command.
 *
 * `venueConfirmed` is what the UI keys off, and it is tri-state on purpose:
 *
 *  - `true`  — the venue acknowledged the effect;
 *  - `false` — the command reached the venue and no acknowledgement came back,
 *              so the operator must reconcile before assuming anything;
 *  - `null`  — the command never touches the venue (start, pause, resume are
 *              local scheduler flags). Reporting those as `false` would put a
 *              permanent «не подтверждено площадкой» warning on commands that
 *              have nothing to confirm, and a warning that is always on is a
 *              warning nobody reads.
 *
 * `replayed` is true when the `Idempotency-Key` had already been used: the
 * response is the FIRST attempt's outcome and no effect ran a second time.
 */
export const CommandResultSchema = z.object({
  commandId: z.string(),
  command: CommandKindSchema,
  status: CommandStatusSchema,
  requestedAt: z.string(),
  executedAt: z.string().nullable(),
  /** Russian operator-facing summary; identical to the audit entry's text. */
  resultRu: z.string(),
  /** Strategy state as it stands AFTER the command, read back from the server. */
  strategyState: StrategyStateSchema,
  venueConfirmed: z.boolean().nullable(),
  replayed: z.boolean(),
});
export type CommandResult = z.infer<typeof CommandResultSchema>;

// --- Server-Sent Events payloads -------------------------------------------

export const SseEventNameSchema = z.enum([
  'dashboard',
  'decision',
  'position',
  'order',
  'incident',
  'connection',
  'heartbeat',
]);
export type SseEventName = z.infer<typeof SseEventNameSchema>;
